import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

export const EASE = [0.16, 1, 0.3, 1];

export const Reveal = ({ children, delay = 0, y = 14, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

export const TrackCorner = ({ className = "" }) => (
  <svg viewBox="0 0 200 200" fill="none" className={className} aria-hidden="true">
    {[48, 84, 120, 156].map((r) => (
      <path
        key={r}
        d={`M ${r} 0 A ${r} ${r} 0 0 1 0 ${r}`}
        stroke="currentColor"
        strokeWidth="1.25"
      />
    ))}
  </svg>
);

export const Chapter = ({ index, label, dark = false }) => (
  <motion.div
    initial={{ opacity: 0, x: -12 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: EASE }}
    className="flex items-center gap-4"
    data-testid={`chapter-${index}`}
  >
    <span className={`font-mono text-xs ${dark ? "text-kn-cyan [text-shadow:0_0_14px_rgba(46,197,230,0.5)]" : "text-kn-blue"}`}>{index}</span>
    <motion.span
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
      className={`h-px w-10 origin-left ${dark ? "rule-dark" : "rule-light"}`}
    />
    <span
      className={`font-mono text-[11px] tracking-[0.28em] uppercase ${
        dark ? "text-white/60" : "text-kn-navy/50"
      }`}
    >
      {label}
    </span>
  </motion.div>
);

export const PrimaryButton = ({ children, onClick, testId, type = "button" }) => (
  <button
    type={type}
    data-testid={testId}
    onClick={onClick}
    className="group inline-flex items-center gap-3 rounded-full bg-brand-gradient px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_-8px_rgba(46,197,230,0.45)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(46,197,230,0.35)] active:scale-[0.98]"
  >
    {children}
    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
  </button>
);

export const GhostButton = ({ children, onClick, testId, dark = true }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`inline-flex items-center gap-3 rounded-full border px-7 py-3.5 text-sm font-semibold backdrop-blur-sm transition-[transform,color,background-color,border-color,box-shadow] duration-300 active:scale-[0.98] ${
      dark
        ? "border-white/25 bg-white/5 text-white hover:border-kn-cyan hover:text-kn-cyan hover:shadow-[0_0_24px_rgba(46,197,230,0.25)]"
        : "border-kn-navy/20 text-kn-navy hover:border-kn-blue hover:text-kn-blue"
    }`}
  >
    {children}
  </button>
);

export const MonoLabel = ({ children, className = "" }) => (
  <span className={`font-mono text-[11px] tracking-[0.28em] uppercase ${className}`}>
    {children}
  </span>
);
